import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { computeFinance } from '@/lib/finance';
import { SERVICES } from '@/mocks/constants';
import ExpenseForm from './components/ExpenseForm';
import { MonthlyChart, ServiceChart, CollectionChart, type ServiceRevenue } from './components/Charts';
import Badge from '@/components/base/Badge';

type Period = 'Bugün' | 'Bu Hafta' | 'Bu Ay' | 'Özel';
type Tab = 'Özet' | 'Gelirler' | 'Giderler';

function dayOf(dateStr: string): number {
  const m = dateStr.match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

export default function Finans() {
  const { jobs, payments, expenses } = useAppStore();
  const [tab, setTab] = useState<Tab>('Özet');
  const [period, setPeriod] = useState<Period>('Bu Ay');
  const [customStart, setCustomStart] = useState('1');
  const [customEnd, setCustomEnd] = useState('31');
  const [expenseOpen, setExpenseOpen] = useState(false);

  const full = computeFinance(jobs, payments, expenses);

  function inPeriod(dateStr: string): boolean {
    const d = dayOf(dateStr);
    if (period === 'Bugün') return d === 26;
    if (period === 'Bu Hafta') return d >= 22;
    if (period === 'Özel') return d >= Number(customStart) && d <= Number(customEnd);
    return true;
  }

  const periodJobs = jobs.filter((j) => inPeriod(j.date));
  const periodPayments = payments.filter((p) => inPeriod(p.date));
  const periodExpenses = expenses.filter((e) => inPeriod(e.date));
  const summary = computeFinance(periodJobs, periodPayments, periodExpenses);

  const serviceRevenue: ServiceRevenue[] = SERVICES.map((s) => ({
    label: s,
    value: jobs.filter((j) => j.service === s).reduce((sum, j) => sum + j.jobValue, 0),
  })).filter((s) => s.value > 0);

  const metricCards = [
    { label: 'İş Bedeli / Ciro', value: summary.ciro, icon: 'ri-briefcase-line', tone: 'bg-primary-50 text-primary-600' },
    { label: 'Tahsil Edilen', value: summary.collected, icon: 'ri-wallet-3-line', tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Açık Alacak', value: summary.openReceivable, icon: 'ri-hourglass-line', tone: 'bg-amber-50 text-amber-600' },
    { label: 'Toplam Gider', value: summary.expense, icon: 'ri-arrow-up-circle-line', tone: 'bg-red-50 text-red-600' },
    { label: 'Net Nakit Sonucu', value: summary.netCash, icon: 'ri-funds-box-line', tone: 'bg-accent-100 text-accent-700' },
  ];

  const periods: Period[] = ['Bugün', 'Bu Hafta', 'Bu Ay', 'Özel'];
  const tabs: Tab[] = ['Özet', 'Gelirler', 'Giderler'];

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Finans
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Gelir & Gider</h1>
          <p className="mt-1 text-sm text-foreground-500">Ciro, tahsilat, alacak ve giderleri tek yerden izleyin.</p>
        </div>
        <button
          type="button"
          onClick={() => setExpenseOpen(true)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line text-base" />
          Gider Ekle
        </button>
      </div>

      {/* Owner monthly summary */}
      <div className="overflow-hidden rounded-lg bg-primary-950">
        <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-xl font-bold text-white">Ağustos 2026</h2>
              <span className="rounded-full bg-accent-500/20 px-2.5 py-0.5 text-xs font-semibold text-accent-300">
                Demo Veri
              </span>
            </div>
            <p className="mt-1 text-sm text-primary-300">Sahip aylık özeti</p>
          </div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-5">
            <div>
              <div className="text-xs font-semibold text-primary-300">İş Bedeli / Ciro</div>
              <div className="mt-1 font-heading text-lg font-bold text-white">{formatTL(full.ciro)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-primary-300">Tahsil Edilen</div>
              <div className="mt-1 font-heading text-lg font-bold text-emerald-400">{formatTL(full.collected)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-primary-300">Açık Alacak</div>
              <div className="mt-1 font-heading text-lg font-bold text-amber-400">{formatTL(full.openReceivable)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-primary-300">Gider</div>
              <div className="mt-1 font-heading text-lg font-bold text-red-400">{formatTL(full.expense)}</div>
            </div>
            <div>
              <div className="text-xs font-semibold text-primary-300">Net Nakit Sonucu</div>
              <div className="mt-1 font-heading text-lg font-bold text-accent-300">{formatTL(full.netCash)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Period selector + summary */}
      <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-background-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {periods.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  period === p ? 'bg-white text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          {period === 'Özel' && (
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <input
                className="h-9 w-16 rounded-md border border-background-200 px-2 text-center text-sm"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <span>—</span>
              <input
                className="h-9 w-16 rounded-md border border-background-200 px-2 text-center text-sm"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <span className="text-xs text-foreground-400">gün</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-5">
          {metricCards.map((m) => (
            <div key={m.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${m.tone}`}>
                <i className={`${m.icon} text-lg`} />
              </div>
              <div className="mt-3 font-heading text-lg font-bold text-foreground-950">{formatTL(m.value)}</div>
              <div className="mt-0.5 text-xs font-semibold text-foreground-500">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-background-200">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
              tab === t
                ? 'border-accent-500 text-foreground-900'
                : 'border-transparent text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Özet' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-white p-5">
            <h3 className="font-heading text-[15px] font-bold text-foreground-950">Aylık Gelir & Gider</h3>
            <p className="mb-4 mt-0.5 text-xs text-foreground-400">Son 6 ay</p>
            <MonthlyChart augustCiro={full.ciro} augustGider={full.expense} />
          </div>
          <div className="rounded-lg border border-background-200 bg-white p-5">
            <h3 className="font-heading text-[15px] font-bold text-foreground-950">Hizmetlere Göre Gelir</h3>
            <p className="mb-4 mt-0.5 text-xs text-foreground-400">İş bedeline göre</p>
            <ServiceChart data={serviceRevenue} />
          </div>
          <div className="rounded-lg border border-background-200 bg-white p-5 lg:col-span-2">
            <h3 className="font-heading text-[15px] font-bold text-foreground-950">Tahsilat Durumu</h3>
            <p className="mb-4 mt-0.5 text-xs text-foreground-400">Tahsil edilen ve açık alacak oranı</p>
            <CollectionChart collected={full.collected} open={full.openReceivable} />
          </div>
        </div>
      )}

      {tab === 'Gelirler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
            <h3 className="font-heading text-[15px] font-bold text-foreground-950">Gelirler</h3>
            <span className="text-xs font-semibold text-foreground-400">{payments.length} kayıt</span>
          </div>
          <ul className="divide-y divide-background-100">
            {payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <i className="ri-arrow-down-circle-line text-lg" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground-900">{p.customerName}</div>
                  <div className="truncate text-xs text-foreground-500">{p.description || 'Tahsilat'}</div>
                </div>
                <Badge tone="neutral">{p.method}</Badge>
                <span className="hidden text-xs text-foreground-400 sm:block">{p.date}</span>
                <span className="font-heading text-sm font-bold text-emerald-600">{formatTL(p.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tab === 'Giderler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
          <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
            <h3 className="font-heading text-[15px] font-bold text-foreground-950">Giderler</h3>
            <span className="text-xs font-semibold text-foreground-400">{expenses.length} kayıt</span>
          </div>
          <ul className="divide-y divide-background-100">
            {expenses.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                  <i className="ri-arrow-up-circle-line text-lg" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground-900">{e.description || e.category}</div>
                  <div className="truncate text-xs text-foreground-500">
                    {e.category}
                    {e.team ? ` · ${e.team}` : ''}
                  </div>
                </div>
                <span className="hidden text-xs text-foreground-400 sm:block">{e.date}</span>
                <span className="font-heading text-sm font-bold text-red-600">-{formatTL(e.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ExpenseForm open={expenseOpen} onClose={() => setExpenseOpen(false)} />
    </div>
  );
}